import { useNavigate, useSearchParams } from "@solidjs/router";
import { createSignal, onCleanup, Show } from "solid-js";
import { EMAIL_RE } from "../../shared/api";
import { api, ApiFailure } from "../api";
import { AppBar } from "../components/AppBar";
import { Button } from "../components/Button";
import { AUTH_ERROR, AUTH_HEADING, AUTH_LINK, AUTH_TEXT, TextField } from "../components/TextField";
import { session } from "../state/session";

const MESSAGES: Record<string, string> = {
  RATE_LIMITED: "Trop de demandes. Réessayez plus tard.",
  MAIL_FAILED: "L'e-mail n'a pas pu être envoyé. Réessayez.",
  INVALID_CODE: "Code incorrect.",
  CODE_EXPIRED: "Ce code a expiré. Demandez un nouveau code.",
  TOO_MANY_ATTEMPTS: "Trop de tentatives. Demandez un nouveau code.",
};

/** Figma G0b "Se connecter avec un e-mail", then a 6-digit code sent by e-mail (stays in the app). */
export function SignIn() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = createSignal("");
  const [code, setCode] = createSignal("");
  const [step, setStep] = createSignal<"email" | "code">("email");
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string>();
  const [cooldown, setCooldown] = createSignal(0);
  const timer = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
  onCleanup(() => clearInterval(timer));

  const fail = (e: unknown, fallback: string) =>
    setError((e instanceof ApiFailure && (MESSAGES[e.code] ?? e.userMessage)) || fallback);

  const sendCode = async (e?: Event) => {
    e?.preventDefault();
    setError(undefined);
    if (!EMAIL_RE.test(email().trim())) return setError("Adresse e-mail invalide.");
    setBusy(true);
    try {
      const r = await api.post<{ ok: true; code?: string }>("/auth/email/code", {
        email: email().trim(),
        ...(typeof params.next === "string" && { next: params.next }),
      });
      setStep("code");
      setCode(r.code ?? ""); // dev only: the server returns the code (DEV_EMAIL_CODE=1)
      setCooldown(60);
    } catch (err) {
      fail(err, "Impossible d'envoyer l'e-mail.");
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e?: Event) => {
    e?.preventDefault();
    if (busy() || code().length !== 6) return;
    setError(undefined);
    setBusy(true);
    try {
      const r = await api.post<{ ok: true; next: string }>("/auth/email/verify", {
        email: email().trim(),
        code: code(),
      });
      await session.refetch();
      navigate(r.next || "/", { replace: true });
    } catch (err) {
      fail(err, "Vérification impossible. Réessayez.");
      setBusy(false);
    }
  };

  return (
    <main class="screen bg-surface">
      <AppBar title="Mon compte" back="/" />
      <form
        class="screen-body px-[15px] pt-8 short:pt-4"
        onSubmit={(e) => (step() === "email" ? sendCode(e) : verify(e))}
      >
        <Show
          when={step() === "code"}
          fallback={
            <>
              <h1 class={AUTH_HEADING}>Veuillez renseigner votre adresse mail.</h1>
              <TextField
                id="email"
                label="Adresse mail :"
                class="mt-[46px] short:mt-6"
                type="email"
                inputmode="email"
                autocomplete="email"
                required
                placeholder="prenom.nom@exemple.fr"
                value={email()}
                onInput={(e) => setEmail(e.currentTarget.value)}
              />
            </>
          }
        >
          <h1 class={AUTH_HEADING}>Saisissez le code reçu par e-mail.</h1>
          <p class={AUTH_TEXT}>
            Un code à 6 chiffres a été envoyé à <span class="text-strong">{email().trim()}</span>.
          </p>
          <TextField
            id="code"
            label="Code :"
            class="mt-6 short:mt-4"
            inputClass="tracking-[0.3em]"
            inputmode="numeric"
            autocomplete="one-time-code"
            maxLength={6}
            placeholder="123456"
            value={code()}
            onInput={(e) => {
              setCode(e.currentTarget.value.replace(/\D/g, "").slice(0, 6));
              // Numeric keypads have no return key: submit on the 6th digit.
              if (code().length === 6) {
                // Close the keyboard first, while the field still exists: the next screen must not
                // inherit the keyboard's layout (iOS sends no focusout for a removed field).
                e.currentTarget.blur();
                void verify();
              }
            }}
          />
          <div class="mt-3 flex justify-between">
            <button type="button" class={AUTH_LINK} onClick={() => setStep("email")}>
              Modifier l'adresse
            </button>
            <button
              type="button"
              class={AUTH_LINK}
              disabled={cooldown() > 0 || busy()}
              onClick={() => void sendCode()}
            >
              {cooldown() > 0 ? `Renvoyer le code (${cooldown()} s)` : "Renvoyer le code"}
            </button>
          </div>
        </Show>
        <Show when={error()}>
          {(m) => (
            <p class={AUTH_ERROR} role="alert">
              {m()}
            </p>
          )}
        </Show>
        <div class="screen-footer">
          <Button
            type="submit"
            loading={busy()}
            disabled={step() === "code" && code().length !== 6}
          >
            {step() === "email" ? "Recevoir le code" : "Confirmer"}
          </Button>
        </div>
      </form>
    </main>
  );
}
